<template>
  <div class="pt-help-widget">

    <transition name="pt-help-panel-fade">
      <div v-if="open" class="pt-help-panel" role="dialog" aria-label="Contact support">
        <div class="pt-help-panel__header">
          <div>
            <div class="pt-help-panel__title">Get help</div>
            <div class="pt-help-panel__subtitle">We'll reply by email, usually within a few hours.</div>
          </div>
          <button class="pt-help-panel__close" @click="close" aria-label="Close">&times;</button>
        </div>

        <div class="pt-help-panel__body">

          <div v-if="status === 'sent'" class="pt-help-panel__success">
            <div class="pt-help-panel__success-title">Message sent!</div>
            <div class="pt-help-panel__success-text">We'll reply to <strong>{{ userEmail || 'your email' }}</strong> shortly.</div>
          </div>

          <template v-else>
            <label class="pt-help-panel__label" for="pt-help-msg">Your message</label>
            <textarea
              id="pt-help-msg"
              ref="messageBox"
              v-model="message"
              :disabled="status === 'sending'"
              class="pt-help-panel__textarea"
              placeholder="Hi! I need help with..."
              rows="6"></textarea>
            <div v-if="status === 'error'" class="pt-help-panel__error">
              Couldn't send your message. Please try again, or email
              <a href="mailto:support@feedback.link">support@feedback.link</a> directly.
            </div>
          </template>

        </div>

        <div class="pt-help-panel__footer">
          <button
            v-if="status === 'sent'"
            class="pt-help-panel__send"
            @click="close">
            Close
          </button>
          <button
            v-else
            class="pt-help-panel__send"
            :disabled="!canSend"
            @click="send">
            <span v-if="status === 'sending'">Sending&hellip;</span>
            <span v-else>Send message</span>
          </button>
        </div>
      </div>
    </transition>

    <button
      class="pt-help-button"
      :class="{ 'pt-help-button--open': open }"
      @click="toggle"
      :aria-label="open ? 'Close help' : 'Get help'">
      <span v-if="!open" class="pt-help-button__qmark">?</span>
      <span v-else class="pt-help-button__close">&times;</span>
    </button>

  </div>
</template>

<script>
import axios from 'axios'
import { auth } from '@/firebase/firebaseConfig.js'

const backendTarget = process.env.VUE_APP_BACKEND_TARGET ||
  (process.env.NODE_ENV === 'development' ? 'localhost' : 'prod')

const DASH_BASE = (backendTarget === 'localhost' || backendTarget === 'devcloud')
  ? 'http://localhost:1000'
  : 'https://feedback.link'

export default {
  name: 'HelpWidget',
  data () {
    return {
      open: false,
      message: '',
      status: 'idle' // idle | sending | sent | error
    }
  },
  computed: {
    userEmail () {
      const user = this.$store.state.auth.firebaseUser
      return (user && user.email) ? user.email : ''
    },
    businessID () {
      try {
        return this.$store.state.dash.viewData.business.businessID || ''
      } catch (e) {
        return ''
      }
    },
    canSend () {
      return this.message.trim().length > 0 && this.status !== 'sending'
    }
  },
  methods: {
    toggle () {
      if (this.open) this.close()
      else this.openPanel()
    },
    openPanel (opts) {
      this.open = true
      if (this.status === 'sent') {
        // Coming back after a successful send — reset for a new message
        this.status = 'idle'
        this.message = ''
      }
      if (opts && typeof opts.text === 'string' && opts.text.length) {
        this.message = opts.text
      }
      this.$nextTick(() => {
        if (this.$refs.messageBox) this.$refs.messageBox.focus()
      })
    },
    close () {
      this.open = false
    },
    onOpenEvent (e) {
      const detail = (e && e.detail) || {}
      this.openPanel(detail)
    },
    async send () {
      if (!this.canSend) return
      this.status = 'sending'
      try {
        const token = await auth.currentUser.getIdToken()
        await axios.post(`${DASH_BASE}/api/dash/sendsupportmessage`, {
          message: this.message,
          businessID: this.businessID
        }, {
          headers: {
            token,
            'Content-Type': 'application/json'
          }
        })
        this.status = 'sent'
      } catch (e) {
        console.error('support send failed', e)
        this.status = 'error'
      }
    }
  },
  mounted () {
    window.addEventListener('fbl:open-help', this.onOpenEvent)
  },
  beforeDestroy () {
    window.removeEventListener('fbl:open-help', this.onOpenEvent)
  }
}
</script>

<style lang="scss" scoped>
.pt-help-widget {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 52000;
}

.pt-help-button {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #cb03e3;
  color: #fff;
  border: none;
  box-shadow: 0 6px 18px rgba(203, 3, 227, 0.45), 0 2px 6px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  padding: 0;
  outline: none;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(203, 3, 227, 0.55), 0 3px 8px rgba(0, 0, 0, 0.14);
  }

  &:active {
    transform: translateY(0);
  }

  &--open {
    background: #a502b8;
  }
}

.pt-help-button__qmark {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  font-family: 'Montserrat', system-ui, sans-serif;
}

.pt-help-button__close {
  font-size: 32px;
  font-weight: 400;
  line-height: 1;
}

.pt-help-panel {
  position: absolute;
  bottom: 72px;
  left: 0;
  width: 360px;
  max-width: calc(100vw - 48px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Montserrat', system-ui, sans-serif;
}

.pt-help-panel__header {
  background: #cb03e3;
  color: #fff;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.pt-help-panel__title {
  font-size: 17px;
  font-weight: 600;
  line-height: 1.2;
}

.pt-help-panel__subtitle {
  font-size: 12px;
  opacity: 0.85;
  margin-top: 4px;
  line-height: 1.4;
}

.pt-help-panel__close {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0.85;

  &:hover { opacity: 1; }
}

.pt-help-panel__body {
  padding: 16px 18px 8px 18px;
}

.pt-help-panel__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 6px;
}

.pt-help-panel__textarea {
  width: 100%;
  border: 1px solid #e0e0e6;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 110px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  color: #2c2c2c;

  &:focus {
    border-color: #cb03e3;
    box-shadow: 0 0 0 3px rgba(203, 3, 227, 0.15);
  }

  &:disabled {
    background: #f7f7fa;
    cursor: not-allowed;
  }
}

.pt-help-panel__error {
  margin-top: 10px;
  font-size: 12px;
  color: #c0392b;
  line-height: 1.4;

  a { color: #c0392b; text-decoration: underline; }
}

.pt-help-panel__success {
  padding: 8px 0 4px 0;
}

.pt-help-panel__success-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c2c2c;
  margin-bottom: 6px;
}

.pt-help-panel__success-text {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.pt-help-panel__footer {
  padding: 8px 18px 16px 18px;
  display: flex;
  justify-content: flex-end;
}

.pt-help-panel__send {
  background: #cb03e3;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;

  &:hover:not(:disabled) {
    background: #a502b8;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    background: #ebadf3;
    cursor: not-allowed;
  }
}

.pt-help-panel-fade-enter-active,
.pt-help-panel-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.pt-help-panel-fade-enter,
.pt-help-panel-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
